import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trip } from '../../types/trip';
import { tripsApi } from '../../api/trips.api';
import { TripRouteMap, TripPoint } from './TripRouteMap';
import { QualificationBadge } from '../ui/Badge';
import { formatDate, formatTime, formatDistance } from '../../utils/formatters';

interface TripDetailModalProps {
  trip: Trip;
  /** Whether the current user is allowed to see GPS route data */
  canViewGps: boolean;
  onClose: () => void;
  onQualify?: (tripId: string, qualification: 'professional' | 'personal') => Promise<void>;
}

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{value}</span>
  </div>
);

type LocationTypeValue = 'domicile' | 'travail' | 'client' | 'lieu_public' | 'autre';

const LOCATION_TYPE_ICONS: Record<LocationTypeValue, string> = {
  domicile: '🏠',
  travail: '💼',
  client: '🤝',
  lieu_public: '📍',
  autre: '❓',
};

const LocationTypeChip: React.FC<{
  value: LocationTypeValue;
  labelKey: string;
}> = ({ value, labelKey }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-800 text-sm font-medium">
    <span aria-hidden="true">{LOCATION_TYPE_ICONS[value]}</span>
    {labelKey}
  </span>
);

const getDriverName = (trip: Trip): string => {
  if (typeof trip.driver_id === 'object' && trip.driver_id) {
    return trip.driver_id.name;
  }
  return trip.driver_name || 'Unknown';
};

const getVehiclePlate = (trip: Trip): string => {
  if (typeof trip.vehicle_id === 'object' && trip.vehicle_id) {
    return trip.vehicle_id.plate_number;
  }
  return trip.vehicle_plate || 'Unknown';
};

export const TripDetailModal: React.FC<TripDetailModalProps> = ({
  trip,
  canViewGps,
  onClose,
  onQualify,
}) => {
  const { t } = useTranslation();
  const [points, setPoints] = useState<TripPoint[]>([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [pointsError, setPointsError] = useState<string | null>(null);
  const [qualifying, setQualifying] = useState(false);

  const isSmartcarTrip = trip.source === 'smartcar';

  const loadPoints = useCallback(async () => {
    // Smartcar trips have no GPS waypoints — skip the fetch entirely
    if (!canViewGps || isSmartcarTrip) return;
    setLoadingPoints(true);
    setPointsError(null);
    try {
      const data = await tripsApi.getTripPoints(trip._id);
      setPoints(data);
    } catch {
      setPointsError(t('trips.detail.mapLoadError'));
    } finally {
      setLoadingPoints(false);
    }
  }, [trip._id, canViewGps, isSmartcarTrip, t]);

  useEffect(() => {
    loadPoints();
  }, [loadPoints]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleQualify = async (qualification: 'professional' | 'personal') => {
    if (!onQualify) return;
    setQualifying(true);
    try {
      await onQualify(trip._id, qualification);
      onClose();
    } finally {
      setQualifying(false);
    }
  };

  const startOdo = trip.start_odometer ?? trip.obd_data?.start_odometer;
  const endOdo = trip.end_odometer ?? trip.obd_data?.end_odometer;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900">{t('trips.detail.title')}</h2>
            <QualificationBadge type={trip.qualification} />
            {isSmartcarTrip && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Smartcar
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Data quality warning banner */}
        {trip.data_quality_flag && (() => {
          const details = trip.data_quality_details ?? {};
          let message: string;
          if (trip.data_quality_reason === 'impossible_speed') {
            const avg = details.avg_speed_kmh != null ? `${Math.round(details.avg_speed_kmh)} km/h` : null;
            message = avg
              ? `Vitesse impossible détectée sur ce trajet (${avg} de moyenne). Les données peuvent être inexactes.`
              : 'Vitesse impossible détectée sur ce trajet. Les données peuvent être inexactes.';
          } else if (trip.data_quality_reason === 'odometer_gps_mismatch') {
            const odo = details.odo_distance_km != null ? `${details.odo_distance_km.toFixed(1)} km` : null;
            const gps = details.gps_distance_km != null ? `${details.gps_distance_km.toFixed(1)} km` : null;
            message = odo && gps
              ? `Écart important entre l'odomètre (${odo}) et le GPS (${gps}). Les données peuvent être inexactes.`
              : 'Écart important détecté entre l\'odomètre et le GPS. Les données peuvent être inexactes.';
          } else {
            message = 'Des anomalies ont été détectées sur ce trajet. Les données peuvent être inexactes.';
          }
          return (
            <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <svg
                className="mt-0.5 w-5 h-5 shrink-0 text-amber-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">Données de trajet suspectes</p>
                <p className="text-sm text-amber-700 mt-0.5">{message}</p>
              </div>
            </div>
          );
        })()}

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Info grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('trips.detail.tripInfo')}
              </h3>
              <div>
                <DetailRow
                  label={t('trips.detail.date')}
                  value={`${formatDate(trip.start_time)} ${formatTime(trip.start_time)}`}
                />
                <DetailRow
                  label={t('trips.detail.driver')}
                  value={getDriverName(trip)}
                />
                <DetailRow
                  label={t('trips.detail.vehicle')}
                  value={getVehiclePlate(trip)}
                />
                <DetailRow
                  label={t('trips.detail.distance')}
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      {formatDistance(trip.distance_km)}
                      {trip.distance_source === 'gps' && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                          title={t('trips.detail.distanceSourceGps')}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          GPS
                        </span>
                      )}
                    </span>
                  }
                />
                <DetailRow
                  label={t('trips.detail.duration')}
                  value={`${trip.duration_minutes} ${t('trips.table.min')}`}
                />
                {trip.obd_device_imei && (
                  <DetailRow
                    label={t('trips.detail.obdImei')}
                    value={<span className="font-mono text-xs">{trip.obd_device_imei}</span>}
                  />
                )}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('trips.detail.performance')}
              </h3>
              <div>
                <DetailRow
                  label={t('trips.detail.avgSpeed')}
                  value={`${trip.avg_speed_kmh ?? '—'} km/h`}
                />
                <DetailRow
                  label={t('trips.detail.maxSpeed')}
                  value={`${trip.max_speed_kmh ?? '—'} km/h`}
                />
                {startOdo && endOdo && (
                  <>
                    <DetailRow
                      label={t('trips.detail.startOdo')}
                      value={`${startOdo.toLocaleString()} km`}
                    />
                    <DetailRow
                      label={t('trips.detail.endOdo')}
                      value={`${endOdo.toLocaleString()} km`}
                    />
                  </>
                )}
                {trip.obd_data?.fuel_used && (
                  <DetailRow
                    label={t('trips.detail.fuelUsed')}
                    value={`${trip.obd_data.fuel_used.toFixed(2)} L`}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Addresses */}
          {(canViewGps || isSmartcarTrip) && (trip.start_address || trip.end_address || trip.start_location?.address || trip.end_location?.address) && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('trips.detail.route')}
              </h3>
              <div className="space-y-2">
                {(trip.start_address || trip.start_location?.address) && (
                  <div className="flex items-start gap-2">
                    <span className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                    <span className="text-sm text-gray-700">{trip.start_address || trip.start_location?.address}</span>
                  </div>
                )}
                {(trip.end_address || trip.end_location?.address) && (
                  <div className="flex items-start gap-2">
                    <span className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                    <span className="text-sm text-gray-700">{trip.end_address || trip.end_location?.address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GPS / Route Map */}
          {canViewGps || isSmartcarTrip ? (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {t('trips.detail.gpsRoute')}
              </h3>

              {/* Smartcar: render GPS waypoints as polyline when available, dashed line as fallback */}
              {isSmartcarTrip ? (() => {
                const gpsPoints = (trip.gps_points || []).map(p => ({
                  lat: p.lat,
                  lon: p.lon,
                  timestamp: p.timestamp,
                  speed: p.speed ?? 0,
                }));
                const startLoc = trip.start_location
                  ? { lat: trip.start_location.latitude, lng: trip.start_location.longitude }
                  : gpsPoints.length > 0
                    ? { lat: gpsPoints[0].lat, lng: gpsPoints[0].lon }
                    : null;
                const endLoc = trip.end_location
                  ? { lat: trip.end_location.latitude, lng: trip.end_location.longitude }
                  : gpsPoints.length > 0
                    ? { lat: gpsPoints[gpsPoints.length - 1].lat, lng: gpsPoints[gpsPoints.length - 1].lon }
                    : null;
                return startLoc && endLoc ? (
                  <TripRouteMap
                    points={gpsPoints}
                    mapId={`trip-map-${trip._id}`}
                    smartcarStart={gpsPoints.length < 2 ? startLoc : undefined}
                    smartcarEnd={gpsPoints.length < 2 ? endLoc : undefined}
                  />
                ) : (
                  <div className="flex items-center justify-center h-20 bg-gray-50 rounded-lg border border-gray-200 text-gray-400 text-sm">
                    {t('trips.detail.noGpsPoints')}
                  </div>
                );
              })() : (
                <>
                  {loadingPoints && (
                    <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-500">
                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        <span className="text-sm">{t('trips.detail.loadingMap')}</span>
                      </div>
                    </div>
                  )}

                  {!loadingPoints && pointsError && (
                    <div className="flex items-center justify-center h-20 bg-red-50 rounded-lg border border-red-200 text-red-600 text-sm gap-2">
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
                      </svg>
                      {pointsError}
                    </div>
                  )}

                  {!loadingPoints && !pointsError && points.length === 0 && (
                    <div className="flex items-center justify-center h-20 bg-gray-50 rounded-lg border border-gray-200 text-gray-400 text-sm">
                      {t('trips.detail.noGpsPoints')}
                    </div>
                  )}

                  {!loadingPoints && !pointsError && points.length > 0 && (
                    <TripRouteMap points={points} mapId={`trip-map-${trip._id}`} />
                  )}
                </>
              )}
            </div>
          ) : null}

          {/* Departure / Arrival location types */}
          {(trip.departure_type || trip.arrival_type) && (() => {
            const dep = trip.departure_type as LocationTypeValue | undefined;
            const arr = trip.arrival_type as LocationTypeValue | undefined;
            return (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  {t('trips.detail.locationType')}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  {dep ? (
                    <LocationTypeChip
                      value={dep}
                      labelKey={t(`trips.detail.locationTypes.${dep}`)}
                    />
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                  <span className="text-gray-400 font-medium" aria-hidden="true">→</span>
                  {arr ? (
                    <LocationTypeChip
                      value={arr}
                      labelKey={t(`trips.detail.locationTypes.${arr}`)}
                    />
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Qualification note */}
          {trip.qualification_note && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                {t('trips.detail.note')}
              </p>
              <p className="text-sm text-gray-700">{trip.qualification_note}</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.close')}
          </button>
          {trip.qualification === 'pending' && onQualify && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => handleQualify('professional')}
                disabled={qualifying}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {qualifying ? t('common.loading') : t('trips.detail.markProfessional')}
              </button>
              <button
                onClick={() => handleQualify('personal')}
                disabled={qualifying}
                className="w-full sm:w-auto px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {qualifying ? t('common.loading') : t('trips.detail.markPersonal')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
